import { useMemo } from 'react';
import { berlinDate, monthGrid, monthOf, SHIFT_STATUS_LABELS, weekday, WEEKDAY_SHORT, type Shift } from '@kontor/shared';
import { abbreviations, employeeHue, hoursDe, shiftTimes } from './format';
import { menuPoint } from './ShiftDayGrid';
import './shifts.css';

interface Props {
  /** Die Tage des Zeitraums: eine Woche (7) oder ein Monat (28 bis 31) */
  days: string[];
  /** Ansicht Monat: das Raster füllt die Wochen mit den Nachbartagen auf, diese erscheinen blass */
  month: boolean;
  shifts: Shift[];
  /** Kürzel vor der Zeit (Plan mehrerer Personen), sonst nur die Zeit */
  showNames: boolean;
  /** Auswahl für Verwalter: Schicht anklicken, Kopf eines Tages wählt alle Schichten des Tages */
  selected?: ReadonlySet<string>;
  onToggle?: (id: string) => void;
  onToggleDay?: (day: string) => void;
  /** Rechtsklick oder Kontextmenü-Taste auf einer Schicht öffnet das Menü der Seite */
  onMenu?: (shift: Shift, x: number, y: number) => void;
}

/**
 * Schichten in Woche und Monat (KON-040): ein Kalenderraster mit einem Feld je Tag, darin die Schichten als kleine Kästchen untereinander, zum Beispiel „AS 8:00 - 16:00“ und
 * „LB 8:45 - 17:45“. Das Kürzel sind die Anfangsbuchstaben des Namens, der volle Name steht im Tooltip und für Screenreader. Entwürfe sind gestrichelt und schraffiert,
 * veröffentlichte Schichten ausgefüllt, jede Person hat ihre feste Farbe. Der Tag mit den Schichten ist anklickbar, um alle auf einmal zu wählen.
 */
export function ShiftBoard({ days, month, shifts, showNames, selected, onToggle, onToggleDay, onMenu }: Props) {
  const today = berlinDate();
  const selectable = !!onToggle;
  const shown = useMemo(() => (month ? monthGrid(monthOf(days[0]!)).days : days), [month, days]);
  const codes = useMemo(() => abbreviations(shifts), [shifts]);
  const byDay = useMemo(() => {
    const map = new Map<string, Shift[]>();
    for (const s of shifts) map.set(s.date, [...(map.get(s.date) ?? []), s]);
    for (const list of map.values()) list.sort((a, b) => a.startTime.localeCompare(b.startTime) || a.employeeName.localeCompare(b.employeeName, 'de'));
    return map;
  }, [shifts]);
  const inPeriod = new Set(days);

  return (
    <div className="sboard-wrap">
      <div className="sboard" role="grid" aria-label="Schichtplan">
        {WEEKDAY_SHORT.map((wd, i) => <div key={wd} className={`sboard__wd${i >= 5 ? ' is-weekend' : ''}`} role="columnheader">{wd}</div>)}
        {shown.map((d) => {
          const list = byDay.get(d) ?? [];
          const outside = !inPeriod.has(d);
          const head = (
            <>
              <strong>{Number(d.slice(8, 10))}</strong>
              {(!month || d.slice(8, 10) === '01') && <span className="sboard__mo">{d.slice(5, 7)}.</span>}
              {list.length > 0 && <span className="sboard__count">{list.length}</span>}
            </>
          );
          return (
            <div key={d} className={`sboard__day${d === today ? ' is-today' : ''}${weekday(d) >= 5 ? ' is-weekend' : ''}${outside ? ' is-outside' : ''}`} role="gridcell">
              {selectable && list.length > 0 ? (
                <button type="button" className="sboard__head is-clickable" onClick={() => onToggleDay?.(d)} aria-label={`Alle Schichten am ${d.slice(8, 10)}.${d.slice(5, 7)}. auswählen`}>{head}</button>
              ) : (
                <div className="sboard__head">{head}</div>
              )}
              <div className="sboard__shifts">
                {list.map((s) => {
                  const on = selected?.has(s.id) ?? false;
                  const text = showNames ? `${codes.get(s.employeeId) ?? '?'} ${shiftTimes(s)}` : shiftTimes(s);
                  const taskText = s.tasks.map((t) => `${t.done ? '✓ ' : ''}${t.title} ${shiftTimes(t)}`);
                  const full = `${s.employeeName}, ${d.slice(8, 10)}.${d.slice(5, 7)}., ${s.startTime} bis ${s.endTime} Uhr, ${hoursDe(s.hours)}, ${SHIFT_STATUS_LABELS[s.status]}${s.note ? `, ${s.note}` : ''}${taskText.length > 0 ? `. Aufgaben im Dienst: ${taskText.join('; ')}` : ''}`;
                  const className = `schip schip--${s.status}${on ? ' is-selected' : ''}`;
                  const style = { ['--hue' as string]: showNames ? employeeHue(s.employeeId) : 170 };
                  const menu = onMenu
                    ? (e: React.MouseEvent<HTMLElement>) => {
                        e.preventDefault();
                        const { x, y } = menuPoint(e);
                        onMenu(s, x, y);
                      }
                    : undefined;
                  return selectable ? (
                    <button key={s.id} type="button" className={className} style={style} title={full} aria-label={full} aria-pressed={on} aria-haspopup={onMenu ? 'menu' : undefined} onClick={() => onToggle?.(s.id)} onContextMenu={menu}>{text}{s.tasks.map((t) => <span key={t.id} className={`schip__task${t.done ? ' is-done' : ''}`}>{t.done ? '✓ ' : ''}{t.title} {shiftTimes(t)}</span>)}</button>
                  ) : (
                    <div key={s.id} className={className} style={style} title={full} aria-label={full}>{text}{s.tasks.map((t) => <span key={t.id} className={`schip__task${t.done ? ' is-done' : ''}`}>{t.done ? '✓ ' : ''}{t.title} {shiftTimes(t)}</span>)}</div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
